import { useState, useCallback, useEffect } from 'react';

import type {
  OrganizationResource,
  GetRequestQueryParamsType
} from '../types/apiTypes';
import { type ResourceType } from '../types/miscTypes';
import useEnsisQuery from './useEnsisQuery';
import usePaginatedList from './usePaginatedList';

const RESOURCE_REFETCH_INTERVAL_MS = 10000;

export const DEFAULT_RESOURCE_SORTING_FIELD = 'Date Added (Newest to Oldest)';
const DEFAULT_SORTING_DIRECTION = 'DESC';
export const RESOURCE_LABEL_TO_DB_FIELD_MAP = new Map<string, string>([
  ['Name (A to Z)', 'name'],
  ['Name (Z to A)', 'name'],
  ['Date Added (Newest to Oldest)', 'created_at'],
  ['Date Added (Oldest to Newest)', 'created_at']
]);

export const RESOURCE_LABEL_TO_DIRECTION = new Map<string, string>([
  ['Name (A to Z)', 'ASC'],
  ['Name (Z to A)', 'DESC'],
  ['Date Added (Newest to Oldest)', 'DESC'],
  ['Date Added (Oldest to Newest)', 'ASC']
]);

interface resourceTableConfig {
  organizationSlug: string
  searchTags?: string[]
  proposalUid?: string
  resourceTypes?: ResourceType[]
  searchTerm?: string
}

const useResourceListData = (config: resourceTableConfig) => {
  const {
    organizationSlug,
    searchTags,
    resourceTypes,
    proposalUid,
    searchTerm
  } = config;

  const getQueryParams = useCallback(() => {
    const queryParams: GetRequestQueryParamsType<'/app/organization-resources'> = {
      organization_slug: organizationSlug ?? '',
      generate_download_url: true,
      exclude_failed_resources: true
    };
    if (searchTags !== undefined && searchTags.length > 0) {
      queryParams.search_tags = searchTags.join('|');
    }
    if (resourceTypes !== undefined && resourceTypes.length > 0) {
      queryParams.resource_types = resourceTypes.join('|');
    }
    if (proposalUid !== undefined && proposalUid !== '') {
      queryParams.proposal_uid = proposalUid;
    }
    if (searchTerm !== undefined && searchTerm !== '') {
      queryParams.search_term = searchTerm;
    }
    return queryParams;
  }, [searchTags, resourceTypes, organizationSlug, proposalUid, searchTerm]);

  const [totalNumberOfResources, setTotalNumberOfResources] = useState<number | null>(null);
  const [refetchingEnabled, setRefetchingEnabled] = useState(false);

  // Only enable sorting when not searching
  const enableSorting = (searchTerm ?? '') === '';

  const {
    formattedData,
    pageCount,
    isLoading,
    pagination,
    onPaginationChange,
    onSortingChange,
    sorting
  } = usePaginatedList<OrganizationResource>({
    defaultSortingField: enableSorting ? DEFAULT_RESOURCE_SORTING_FIELD : '',
    defaultSortDirection: DEFAULT_SORTING_DIRECTION,
    route: '/app/organization-resources',
    queryParams: getQueryParams(),
    labelToDBFieldMap: RESOURCE_LABEL_TO_DB_FIELD_MAP,
    queryConfig: {
      refetchInterval: refetchingEnabled ? RESOURCE_REFETCH_INTERVAL_MS : undefined
    }
  });

  // second resource query that doesn't include filters to always be up to date with total number of resources
  const totalNumOfResources = useEnsisQuery('/app/organization-resources', {
    queryParams: {
      organization_slug: organizationSlug ?? '',
      exclude_failed_resources: true
    }
  }).data?.total ?? 0;

  useEffect(() => {
    setTotalNumberOfResources(totalNumOfResources);
  }, [totalNumOfResources]);

  // Reset page index when filters change
  useEffect(() => {
    onPaginationChange({ ...pagination, pageIndex: 0 });
  }, [getQueryParams]);

  const shouldRefetch = formattedData.items.some(
    (i) => i.processing_status === 'PROCESSING'
  );
  if (refetchingEnabled !== shouldRefetch) {
    setRefetchingEnabled(shouldRefetch);
  }
  const setPageIndex = (index: number) => {
    onPaginationChange({ ...pagination, pageIndex: index });
  };
  return {
    resources: formattedData.items,
    isLoading,
    pageCount,
    totalNumberOfResources,
    pagination,
    setPageIndex,
    sorting,
    onSortingChange
  };
};

export default useResourceListData;
